import * as jsPdf from 'jspdf';
import * as moment from 'moment-timezone';
import { IExternalStorage, ITest } from 'src/Interfaces/TestInterface';
import { IWell, IWellCloseReview } from 'src/Interfaces/WellInterface';
import { fillEmptyRows } from '../MudReport/PopDataMud';
import { textArrayInBoxes, textCenteredInBox, textLeftJustifiedInBox, textRightJustifiedInBox } from '../PopulateDataMethods';
import * as MM from './SpecMilsVolume';

export const createVolumeReport = (tests: ITest[], well: IWell, wellCloseReview: IWellCloseReview | undefined, logoBlob: HTMLImageElement, volumeReport: jsPdf) => {
    volumeReport.setFillColor(MM.HEADER_COLOR);
    const sortedTests = tests.sort((a, b) => a.testDate - b.testDate);
    popReport(sortedTests, well, wellCloseReview, logoBlob, volumeReport);
};

export const drawHeader = (test: ITest, well: IWell, logoBlob: HTMLImageElement, volumeReport: jsPdf) => {
    volumeReport.addImage(logoBlob, 'PNG', MM.LOGO_LEFT, MM.LOGO_TOP, MM.LOGO_WIDTH, MM.LOGO_HEIGHT);
    popOperatorContractorDepth(test, well, volumeReport);
};

const popOperatorContractorDepth = (test: ITest, well: IWell, volumeReport: jsPdf) => {
    const boldText = [
        [`Operator: ${well.operator.operatorName}`, `Contractor: ${well.contractor.contractorName}`],
    ];
    fillEmptyRows(boldText, 6);
    const legal = well.state === 'TX'
        ? 'Section ' + well.shlSection + ', Block ' + well.shlBlock + well.shlBlockDirection + ', Township ' + well.shlTownship + well.shlTownshipDirection
        : 'Section ' + well.shlSection + ', Range ' + well.shlRange + well.shlRangeDirection + ', Township ' + well.shlTownship + well.shlTownshipDirection;
    const normalText = [
        [undefined],
        [`Well Name: ${well.wellName}`, `Rig #: ${well.rigNumber}`],
        [`API: ${well.apiNumber}`, `Spud Date: ${well.spudDate}`],
        [`Legal: ${legal}`, `Finish Date: ${getFinishDate(test, well)}`],
        [`County: ${well.countyParish}`, `Drilling Days: ${getDrillingDays(test, well)}`],
        [`State: ${well.state}`, undefined]
    ];
    volumeReport.setFontSize(MM.HEADER_1_SIZE);
    volumeReport.setFontStyle('bold');
    textArrayInBoxes(boldText, MM.OPERATOR_CONTRACTOR_LEFT, MM.OPERATOR_CONTRACTOR_TOP, MM.OPERATOR_CONTRACTOR_WIDTH, MM.OPERTAOR_CONTRACTOR_HEIGHT, 'left', volumeReport);
    volumeReport.setFontStyle('normal');
    textArrayInBoxes(normalText, MM.OPERATOR_CONTRACTOR_LEFT, MM.OPERATOR_CONTRACTOR_TOP, MM.OPERATOR_CONTRACTOR_WIDTH, MM.OPERTAOR_CONTRACTOR_HEIGHT, 'left', volumeReport);

    const depthArr = [
        [undefined],
        ['MD:', test.depthMD],
        ['TVD:', test.depthTVD],
        [undefined],
    ];

    volumeReport.setFontStyle('bold');
    textArrayInBoxes(
        depthArr,
        MM.DEPTH_LEFT,
        MM.DEPTH_TOP,
        MM.DEPTH_WIDTH,
        MM.DEPTH_HEIGHT,
        'left',
        volumeReport
    );
};

const getFinishDate = (test: ITest, well: IWell) => {
    if (test && test.testDate) {
        return moment.unix(Number(test.testDate)).tz(well.timeZoneName).format('YYYY-MM-DD');
    }
    
    return 'No End Set.';
};

const adjustForYearEnd = (start: moment.Moment, end: moment.Moment, currDays: number) => {
    if (end.year() !== start.year())
    {
        if (start.isLeapYear())
        {
            return currDays + 366;
        } else {
            return currDays + 365;
        }
    }

    return currDays;
};

const getDrillingDays = (test: ITest, well: IWell) => {
    let days = 0;
    const testIntervals = test.intervals.slice().reverse();
    for (let i = 0; i < testIntervals.length; i++) {
        if (testIntervals[i].preset) {
            continue;
        }
        const currInterval = testIntervals[i];
           
        if (currInterval.startDate !== '' && currInterval.stopDate !== '') {
            const currStartDate = moment.unix(Number(currInterval.startDate)).tz(well.timeZoneName);
            const currStopDate = moment.unix(Number(currInterval.stopDate)).tz(well.timeZoneName);
            let currDays = currStopDate.dayOfYear() - currStartDate.dayOfYear() + 1;
            currDays = adjustForYearEnd(currStartDate, currStopDate, currDays);
            // if (i === 0) {
            //     if (well.spudDate) {
            //         const wellSpudDate = moment.tz(well.spudDate, well.timeZoneName);
            //         currDays = currStopDate.dayOfYear() - wellSpudDate.dayOfYear() + 1;
            //         currDays = adjustForYearEnd(wellSpudDate, currStopDate, currDays);
            //     }
            // }
            if ( i < testIntervals.length - 1 ) {
                const nextInterval = testIntervals[i + 1];
                const startDate = moment.unix(Number(nextInterval.startDate));
                const stopDate = moment.unix(Number(currInterval.stopDate));
                if( stopDate.dayOfYear() === startDate.dayOfYear()) {
                    currDays -= 1;
                }
            }

            days += currDays;
        }
    }
    return days;
};

const popReport = (tests: ITest[], well: IWell, wellCloseReview: IWellCloseReview | undefined, logoBlob: HTMLImageElement, volumeReport: jsPdf) => {
    const externalStorageList = getExternalStorageTypes(tests);

    drawHeader(tests[tests.length - 1], well, logoBlob, volumeReport);
    popRowHeaders(volumeReport);
    popExternalHeaders(externalStorageList, volumeReport);
    popAdditionsHeaders(volumeReport);
    popLossesHeaders(volumeReport);
    popFinalVolHeader(volumeReport);
    drawHeaderLines(volumeReport);
    popFooterInfo(tests[tests.length - 1], volumeReport);
    let retIndex = popDataColumns(tests, well, wellCloseReview, 0, volumeReport);
    while (retIndex < tests.length) {
        volumeReport.addPage();
        drawHeader(tests[tests.length - 1], well, logoBlob, volumeReport);
        popRowHeaders(volumeReport);
        popExternalHeaders(externalStorageList, volumeReport);
        popAdditionsHeaders(volumeReport);
        popLossesHeaders(volumeReport);
        popFinalVolHeader(volumeReport);
        drawHeaderLines(volumeReport);
        popFooterInfo(tests[tests.length - 1], volumeReport);
        retIndex = popDataColumns(tests, well, wellCloseReview, retIndex, volumeReport);
    }
};


const popFooterInfo = (lastTest: ITest, volumeReport: jsPdf) => {
    volumeReport.setLineWidth(MM.THIN_LINE);
    volumeReport.setFillColor(MM.HEADER_COLOR);
    volumeReport.rect(MM.WELL_TOTALS_LEFT, MM.WELL_TOTALS_TOP, MM.WELL_TOTALS_WIDTH, MM.ROW_SECTION_HEADER_HEIGHT, 'F');
    volumeReport.rect(MM.WELL_TOTALS_LEFT, MM.WELL_TOTALS_TOP, MM.WELL_TOTALS_WIDTH, MM.WELL_TOTALS_HEIGHT);
    volumeReport.setFontStyle('bold');
    textCenteredInBox('Well Totals', MM.WELL_TOTALS_LEFT, MM.WELL_TOTALS_TOP, MM.WELL_TOTALS_WIDTH, MM.ROW_SECTION_HEADER_HEIGHT, volumeReport);
    const boldHeaderArr = [
        [undefined],
        ['Fluid'],
        [undefined],
        [undefined],
        ['Interval'],
        [undefined],
        [undefined],
        ['Well'],
        [undefined],
        [undefined],
    ];

    textArrayInBoxes(
        boldHeaderArr,
        MM.WELL_TOTALS_LEFT,
        MM.WELL_TOTALS_TOP + MM.ROW_SECTION_HEADER_HEIGHT,
        MM.WELL_TOTALS_WIDTH,
        MM.WELL_TOTALS_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT,
        'left',
        volumeReport
    );

    let lineTop = MM.WELL_TOTALS_TOP 
            + MM.ROW_SECTION_HEADER_HEIGHT 
            + (MM.WELL_TOTALS_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT) / 10 * 3;
    volumeReport.line(
        MM.WELL_TOTALS_LEFT + MM.INDENT_WITDH,
        lineTop,
        MM.COLUMN_START_LEFT,
        lineTop,
    );

    lineTop = MM.WELL_TOTALS_TOP 
            + MM.ROW_SECTION_HEADER_HEIGHT 
            + (MM.WELL_TOTALS_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT) / 10 * 6;

    volumeReport.line(
        MM.WELL_TOTALS_LEFT + MM.INDENT_WITDH,
        lineTop,
        MM.COLUMN_START_LEFT,
        lineTop,
    );

    lineTop = MM.WELL_TOTALS_TOP 
            + MM.ROW_SECTION_HEADER_HEIGHT 
            + (MM.WELL_TOTALS_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT) / 10 * 9;

    volumeReport.line(
        MM.WELL_TOTALS_LEFT + MM.INDENT_WITDH,
        lineTop,
        MM.COLUMN_START_LEFT,
        lineTop,
    );
    
    
    const normalHeaderArr = [
        [undefined],
        [undefined],
        ['Cumulative Base Oil Used (gal)'],
        [undefined],
        [undefined],
        ['Cumulative Base Oil Used (gal)'],
        [undefined],
        [undefined],
        ['Cumulative Base Oil Used (gal)'],
        [undefined],
    ];

    volumeReport.setFontStyle('normal');
    textArrayInBoxes(
        normalHeaderArr,
        MM.WELL_TOTALS_LEFT + MM.INDENT_WITDH - 1,
        MM.WELL_TOTALS_TOP + MM.ROW_SECTION_HEADER_HEIGHT,
        MM.WELL_TOTALS_WIDTH,
        MM.WELL_TOTALS_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT,
        'left',
        volumeReport
    );
    
    const intervals = lastTest.intervals;
    let colNum = 0;
    for (const interval of intervals) {
        const headerArr = [
            [undefined],
            [undefined],
            [undefined],
            [undefined],
            [interval.intervalType],
        ];

        const dataArr = [
            [undefined],
            [undefined],
            [undefined],
            [undefined],
            [undefined],
            [interval.cumBaseOilUsed + (colNum === 0 ? lastTest.volumeAccounting.baseOilUsed : 0)]
        ];
        fillEmptyRows(headerArr, 10);
        fillEmptyRows(dataArr, 10);
        volumeReport.setFontStyle('bold');
        textArrayInBoxes(
            headerArr,
            MM.COLUMN_START_LEFT + MM.WELL_TOTALS_COL_WIDTH * colNum,
            MM.WELL_TOTALS_TOP + MM.ROW_SECTION_HEADER_HEIGHT,
            MM.WELL_TOTALS_COL_WIDTH,
            MM.WELL_TOTALS_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT,
            'center',
            volumeReport
        );
        volumeReport.setFontStyle('normal');

        textArrayInBoxes(
            dataArr,
            MM.COLUMN_START_LEFT + MM.WELL_TOTALS_COL_WIDTH * colNum,
            MM.WELL_TOTALS_TOP + MM.ROW_SECTION_HEADER_HEIGHT,
            MM.WELL_TOTALS_COL_WIDTH,
            MM.WELL_TOTALS_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT,
            'right',
            volumeReport
        );

        const boxTop = MM.WELL_TOTALS_TOP 
            + MM.ROW_SECTION_HEADER_HEIGHT 
            + (MM.WELL_TOTALS_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT) / 10 * 5;
        volumeReport.rect(
            MM.COLUMN_START_LEFT + MM.WELL_TOTALS_COL_WIDTH * colNum,
            boxTop,
            MM.WELL_TOTALS_COL_WIDTH,
            (MM.WELL_TOTALS_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT) / 10
        );
        colNum++;
    }

    const fluidTypes = lastTest.volumeAccounting.propertyTypeCumBaseOilUseds;
    colNum = 0;
    for (const fluidType of fluidTypes) {
        const headerArr = [
            [undefined],
            [fluidType.propertyType],
        ];

        const dataArr = [
            [undefined],
            [undefined],
            [fluidType.cumBaseOilUsed + (fluidType.propertyType === lastTest.propertyType ? lastTest.volumeAccounting.baseOilUsed : 0)]
        ];
        fillEmptyRows(dataArr, 10);
        fillEmptyRows(headerArr, 10);

        volumeReport.setFontStyle('bold');
        textArrayInBoxes(
            headerArr,
            MM.COLUMN_START_LEFT + MM.WELL_TOTALS_COL_WIDTH * colNum,
            MM.WELL_TOTALS_TOP + MM.ROW_SECTION_HEADER_HEIGHT,
            MM.WELL_TOTALS_COL_WIDTH,
            MM.WELL_TOTALS_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT,
            'center',
            volumeReport
        );
        volumeReport.setFontStyle('normal');
        textArrayInBoxes(
            dataArr,
            MM.COLUMN_START_LEFT + MM.WELL_TOTALS_COL_WIDTH * colNum,
            MM.WELL_TOTALS_TOP + MM.ROW_SECTION_HEADER_HEIGHT,
            MM.WELL_TOTALS_COL_WIDTH,
            MM.WELL_TOTALS_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT,
            'right',
            volumeReport
        );
        const boxTop = MM.WELL_TOTALS_TOP 
        + MM.ROW_SECTION_HEADER_HEIGHT 
        + (MM.WELL_TOTALS_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT) / 10 * 2;
        volumeReport.rect(
            MM.COLUMN_START_LEFT + MM.WELL_TOTALS_COL_WIDTH * colNum,
            boxTop,
            MM.WELL_TOTALS_COL_WIDTH,
            (MM.WELL_TOTALS_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT) / 10
        );
        colNum++;
    }

    const wellBoxTop = MM.WELL_TOTALS_TOP 
    + MM.ROW_SECTION_HEADER_HEIGHT 
    + (MM.WELL_TOTALS_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT) / 10 * 8;
    volumeReport.rect(
        MM.COLUMN_START_LEFT,
        wellBoxTop,
        MM.WELL_TOTALS_COL_WIDTH,
        (MM.WELL_TOTALS_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT) / 10
    );

    const wellTotal = [
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        [lastTest.volumeAccounting.cumBaseOilUsed],
        [undefined],
    ];

    textArrayInBoxes(
        wellTotal,
        MM.COLUMN_START_LEFT,
        MM.WELL_TOTALS_TOP + MM.ROW_SECTION_HEADER_HEIGHT,
        MM.WELL_TOTALS_COL_WIDTH,
        MM.WELL_TOTALS_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT,
        'right',
        volumeReport
    );

};
const getExternalStorageTypes = (tests: ITest[]) => {
    return tests.reduce((accumulator, currTest) => {
        if (currTest.externalStorage) {
            currTest.externalStorage.forEach(element => {
                if (accumulator.indexOf(element.fluidType) < 0) {
                    accumulator.push(element.fluidType);
                }
            });
        }
        return accumulator;
    }, [] as string[]).sort();
};

class VolAcctColumn {
    public fluidType: string;
    public testDate: string;
    public testTime: string;
    public volumeOwedToPlant: number = 0;
    public startingReportVolume: number = 0;
    public newHoleVolume: number = 0;
    public footageDrilled: number = 0;
    public additionsTotal: number = 0;
    public waterAddition: number = 0;
    public chemicalAddition: number = 0;
    public slurryAddition: number = 0;
    public washoutAddition: number = 0;
    public otherAddition: number = 0;
    public plantVolumeReceived: number = 0;
    public otherVolumeReceived: number = 0;
    public baseOilAddition: number = 0;
    public startingBaseOil: number = 0;
    public receivedBaseOil: number = 0;
    public endingBaseOil: number = 0;
    public usedBaseOil: number = 0;
    public lossesTotal: number = 0;
    public lossToSurface: number = 0;
    public lossToFormation: number = 0;
    public solidsLoss: number = 0;
    public lossToCuttings: number = 0;
    public mudLeftInHole: number = 0;
    public otherLoss: number = 0;
    public plantVolumeReturned: number = 0;
    public otherVolumeTransfered: number = 0;
    public finalVolume: number = 0;
    public externalStorage: IExternalStorage[] = [];

    constructor(test?: ITest, well?: IWell) {
        if (test && well) {

            const va = test.volumeAccounting;
            this.fluidType = test.propertyType;
            this.testDate = moment.unix(test.testDate).tz(well.timeZoneName).format('M/D/YYYY');
            this.testTime = moment.unix(test.testDate).tz(well.timeZoneName).format('h:mm a');
            this.volumeOwedToPlant = Number(va.volOwedToPlant);
            this.startingReportVolume = Number(va.previousTotalHoleVolume);
                this.newHoleVolume = Number(va.newHoleVolume);
            this.footageDrilled = Number(va.footageDrilled);
            this.additionsTotal = Number(va.totalAdditions);
            this.waterAddition = Number(va.water);
            this.chemicalAddition = Number(va.chemicalAdditions);
            this.slurryAddition = Number(va.slurry);
            this.washoutAddition = Number(va.washout);
            this.otherAddition = Number(va.otherAdditions);
            this.plantVolumeReceived = Number(va.plantVolRecvd);
            this.otherVolumeReceived = Number(va.otherReceived);
            this.baseOilAddition = Number(va.baseOil);
            this.startingBaseOil = Number(va.startingBaseOil);
            this.receivedBaseOil = Number(va.baseOilReceived);
            this.endingBaseOil = Number(va.endingBaseOil);
            this.usedBaseOil = Number(va.baseOilUsed);
            this.lossesTotal = Number(va.totalLosses);
            this.lossToSurface = Number(va.lossToSurface);
            this.lossToFormation = Number(va.lossToFormation);
            this.solidsLoss = Number(va.solidsControl);
            this.lossToCuttings = Number(va.lossToCuttings);
            this.mudLeftInHole = Number(va.mudLeftInHole);
            this.otherLoss = Number(va.otherLoss);
            this.plantVolumeReturned = Number(va.plantVolReturned);
            this.otherVolumeTransfered = Number(va.otherTransfer);
            this.finalVolume = Number(va.totalVolume);
            this.externalStorage = test.externalStorage;
        } else {
            this.fluidType = '';
            this.testDate = '';
            this.testTime = '';
            this.volumeOwedToPlant = 0;
            this.startingReportVolume = 0;
            this.newHoleVolume = 0;
            this.footageDrilled = 0;
            this.additionsTotal = 0;
            this.waterAddition = 0;
            this.chemicalAddition = 0;
            this.slurryAddition = 0;
            this.washoutAddition = 0;
            this.otherAddition = 0;
            this.plantVolumeReceived = 0;
            this.otherVolumeReceived = 0;
            this.baseOilAddition = 0;
            this.startingBaseOil = 0;
            this.receivedBaseOil = 0;
            this.endingBaseOil = 0;
            this.usedBaseOil = 0;
            this.lossesTotal = 0;
            this.lossToSurface = 0;
            this.lossToFormation = 0;
            this.solidsLoss = 0;
            this.lossToCuttings = 0;
            this.mudLeftInHole = 0;
            this.otherLoss = 0;
            this.plantVolumeReturned = 0;
            this.otherVolumeTransfered = 0;
            this.finalVolume = 0;
            this.externalStorage = [];
        }
    }

    public add = (addend: VolAcctColumn) => {
        this.testDate = 'Totals';
        this.testTime = '';
        this.volumeOwedToPlant = Number(addend.volumeOwedToPlant);
        this.startingReportVolume = Number(addend.startingReportVolume);
        this.newHoleVolume += Number(addend.newHoleVolume);
        this.footageDrilled += Number(addend.footageDrilled);
        this.additionsTotal += Number(addend.additionsTotal);
        this.waterAddition += Number(addend.waterAddition);
        this.chemicalAddition += Number(addend.chemicalAddition);
        this.slurryAddition += Number(addend.slurryAddition);
        this.washoutAddition += Number(addend.washoutAddition);
        this.otherAddition += Number(addend.otherAddition);
        this.plantVolumeReceived += Number(addend.plantVolumeReceived);
        this.otherVolumeReceived += Number(addend.otherVolumeReceived);
        this.baseOilAddition += Number(addend.baseOilAddition);
        this.receivedBaseOil += Number(addend.receivedBaseOil);
        this.endingBaseOil = Number(addend.endingBaseOil);
        this.usedBaseOil += Number(addend.usedBaseOil);
        this.lossesTotal += Number(addend.lossesTotal);
        this.lossToSurface += Number(addend.lossToSurface);
        this.lossToFormation += Number(addend.lossToFormation);
        this.solidsLoss += Number(addend.solidsLoss);
        this.lossToCuttings += Number(addend.lossToCuttings);
        this.mudLeftInHole += Number(addend.mudLeftInHole);
        this.otherLoss += Number(addend.otherLoss);
        this.plantVolumeReturned += Number(addend.plantVolumeReturned);
        this.otherVolumeTransfered += Number(addend.otherVolumeTransfered);
        this.finalVolume = Number(addend.finalVolume);
        this.externalStorage = addend.externalStorage;
    }
}

const popDataColumns = (tests: ITest[], well: IWell, wellCloseReview: IWellCloseReview | undefined, startIndex: number, volumeReport: jsPdf)=> {
    const externalStorageList = getExternalStorageTypes(tests);
    let colNum = 0;
    let lastTestType = tests[startIndex].propertyType;
    const fluidChangeTotals = [] as VolAcctColumn[];
    const allTotals: VolAcctColumn = new VolAcctColumn();
    let i;
    for (i = 0; i < tests.length; i++) {
        const isLastTest = i === tests.length -1;
        const currVolAccount = new VolAcctColumn(tests[i], well);
        if (tests[i].isVolumeAccountingReport !== true && !isLastTest && Number(tests[i].volumeAccounting.baseOilUsed) === 0) {
            continue;
        }
        
        if (tests[i].isVolumeAccountingReport || Number(tests[i].volumeAccounting.baseOilUsed) > 0) {
            allTotals.add(currVolAccount);
        }
        const columnAddNumber = wellCloseReview === undefined ? 0 : 2;
        if (i === tests.length - 1) {
            if (lastTestType === currVolAccount.fluidType) {
                if (MM.COLUMN_START_LEFT + MM.COLUMN_WIDTH * ( colNum + 3 + columnAddNumber ) < MM.FULL_WIDTH) {
                    if (tests[i].isVolumeAccountingReport) {
                        createOneTestColumn(currVolAccount, colNum++, false, externalStorageList, volumeReport);
                    }
                    const typeTotal = fluidChangeTotals.find(vac => vac.fluidType === currVolAccount.fluidType);
                    if (typeTotal) {
                        if (tests[i].isVolumeAccountingReport) {
                            typeTotal.add(currVolAccount);
                        }
                        createOneTestColumn(typeTotal, colNum++, true, externalStorageList, volumeReport);
                    } else {
                        createOneTestColumn(currVolAccount, colNum++, true, externalStorageList, volumeReport);
                    }
                    createOneTestColumn(allTotals, colNum++, true, externalStorageList, volumeReport, true);
                    if (wellCloseReview !== undefined) {
                        createWrapUpColumn(wellCloseReview, allTotals, colNum++, externalStorageList, volumeReport);
                    }
                } else {
                    return i;
                }
            } else {
                if (MM.COLUMN_START_LEFT + MM.COLUMN_WIDTH * ( colNum + 5 + columnAddNumber ) < MM.FULL_WIDTH) {
                    const lastTypeTotal = fluidChangeTotals.find(vac => vac.fluidType === lastTestType);
                    if (lastTypeTotal) {
                        createOneTestColumn(lastTypeTotal, colNum++, true, externalStorageList, volumeReport);
                    }
                    if (tests[i].isVolumeAccountingReport) {
                        createOneTestColumn(currVolAccount, colNum++, false, externalStorageList, volumeReport);
                    }
                    const typeTotal = fluidChangeTotals.find(vac => vac.fluidType === currVolAccount.fluidType);
                    if (typeTotal) {
                        if (tests[i].isVolumeAccountingReport) {
                            typeTotal.add(currVolAccount);
                        }
                        createOneTestColumn(typeTotal, colNum++, true, externalStorageList, volumeReport);
                    } else {
                        createOneTestColumn(currVolAccount, colNum++, true, externalStorageList, volumeReport);
                    }
                    createOneTestColumn(allTotals, colNum++, true, externalStorageList, volumeReport, true);
                    if (wellCloseReview !== undefined) {
                        createWrapUpColumn(wellCloseReview, allTotals, colNum++, externalStorageList, volumeReport);
                    }
                } else {
                    return i;
                }
            }
        } else if (i >= startIndex) {
            if( MM.COLUMN_START_LEFT + MM.COLUMN_WIDTH * colNum < MM.FULL_WIDTH ) {
                if(lastTestType === currVolAccount.fluidType) {
                    createOneTestColumn(currVolAccount, colNum++, false, externalStorageList, volumeReport);
                } else if (MM.COLUMN_START_LEFT + MM.COLUMN_WIDTH * ( colNum + 1 ) < MM.FULL_WIDTH) {
                    const lastTypeTotal = fluidChangeTotals.find(vac => vac.fluidType === lastTestType);
                    if (lastTypeTotal) {
                        createOneTestColumn(lastTypeTotal, colNum++, true, externalStorageList, volumeReport);
                    }
                    lastTestType = currVolAccount.fluidType;
                    createOneTestColumn(currVolAccount, colNum++, false, externalStorageList, volumeReport);
                } else {
                    return i;
                }
            } else {
                return i;
            }
        }
        lastTestType = currVolAccount.fluidType;
        const cumVolAccount = fluidChangeTotals.find(vac => vac.fluidType === currVolAccount.fluidType);
        if (cumVolAccount) {
            cumVolAccount.add(currVolAccount);
        } else {
            fluidChangeTotals.push(currVolAccount);
        }
    }
    return i;
};

const createOneTestColumn = (column: VolAcctColumn, colNum:number, isTotal:boolean, externalStorageList: string[], volumeReport: jsPdf, isFinal?: boolean) => {
    const tempLeft = MM.COLUMN_START_LEFT + MM.COLUMN_WIDTH * colNum + 0.5;
    if (isFinal) {
        volumeReport.setFillColor(MM.HEADER_COLOR_LIGHTEST);
        volumeReport.rect(
            tempLeft,
            MM.ROW_HEADERS_BIG_TOP,
            MM.COLUMN_WIDTH,
            MM.ROW_HEADERS_BIG_HEIGHT + MM.EXTERNAL_STORAGE_HEIGHT + MM.ADDITIONS_HEIGHT + MM.LOSSES_HEIGHT + MM.FINAL_VOL_HEIGHT + MM.SECTION_GAP_WIDTH * 4,
            'F'
        );

        volumeReport.setDrawColor('#000000');
        volumeReport.line(
            tempLeft,
            MM.ROW_HEADERS_BIG_TOP,
            tempLeft,
            MM.ROW_HEADERS_BIG_TOP + MM.ROW_HEADERS_BIG_HEIGHT + MM.EXTERNAL_STORAGE_HEIGHT + MM.ADDITIONS_HEIGHT + MM.LOSSES_HEIGHT + MM.FINAL_VOL_HEIGHT + MM.SECTION_GAP_WIDTH * 4
        );
    }
    
    if (isTotal && !isFinal) {
        volumeReport.setFillColor(MM.HEADER_COLOR_FINAL);
        volumeReport.rect(
            tempLeft,
            MM.ROW_HEADERS_BIG_TOP,
            MM.COLUMN_WIDTH,
            MM.ROW_HEADERS_BIG_HEIGHT + MM.EXTERNAL_STORAGE_HEIGHT + MM.ADDITIONS_HEIGHT + MM.LOSSES_HEIGHT + MM.FINAL_VOL_HEIGHT + MM.SECTION_GAP_WIDTH * 4,
            'F'
        );
    }

    const headerArrBoldCentered = [
        [isFinal ? 'Final' : isTotal ? 'Fluid' : column.fluidType],
        [isTotal ? 'Totals' : column.testDate],
        [isTotal ? '' : column.testTime],
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        [undefined],
    ];

    const headerArrBoldRight = [
        [undefined],
        [undefined],
        [undefined],
        [column.volumeOwedToPlant],
        [column.startingReportVolume],
        [undefined],
        [undefined],
        [undefined],
    ];

    const headerArrNormalRight = [
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        [column.newHoleVolume],
        [column.footageDrilled],
    ];
    volumeReport.setFontStyle('bold');
    textArrayInBoxes(
        headerArrBoldCentered,
        tempLeft,
        MM.ROW_HEADERS_BIG_TOP,
        MM.COLUMN_WIDTH - 1,
        MM.ROW_HEADERS_BIG_HEIGHT,
        'center',
        volumeReport
    );

    textArrayInBoxes(
        headerArrBoldRight,
        tempLeft,
        MM.ROW_HEADERS_BIG_TOP,
        MM.COLUMN_WIDTH,
        MM.ROW_HEADERS_BIG_HEIGHT,
        'right',
        volumeReport
    );
    volumeReport.setFontStyle('normal');
    textArrayInBoxes(
        headerArrNormalRight,
        tempLeft,
        MM.ROW_HEADERS_BIG_TOP,
        MM.COLUMN_WIDTH,
        MM.ROW_HEADERS_BIG_HEIGHT,
        'right',
        volumeReport
    );

    drawColLines(MM.COLUMN_START_LEFT + MM.COLUMN_WIDTH * colNum, MM.COLUMN_WIDTH, volumeReport);


    const externalStorageArr = popExternalStorageForCol(externalStorageList, column);
    fillEmptyRows(externalStorageArr, MM.EXTERNAL_STORAGE_ROWS);
    textArrayInBoxes(
        externalStorageArr,
        tempLeft,
        MM.EXTERNAL_STORAGE_TOP + MM.ROW_SECTION_HEADER_HEIGHT,
        MM.COLUMN_WIDTH,
        MM.EXTERNAL_STORAGE_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT,
        'right',
        volumeReport
    );

    const sum = externalStorageArr.reduce((total, curr) => {
        const currNumber = curr[0];
        if (currNumber) {
            return total + currNumber;
        }
        return total;
    }, 0);
    volumeReport.setFontStyle('bold');
    textRightJustifiedInBox(sum.toFixed(0), tempLeft, MM.EXTERNAL_STORAGE_TOP, MM.COLUMN_WIDTH, MM.ROW_SECTION_HEADER_HEIGHT, volumeReport);
        

    const additionArr = [
        [column.waterAddition],
        [column.chemicalAddition],
        [column.slurryAddition],
        [column.washoutAddition],
        [column.otherAddition],
        [column.plantVolumeReceived],
        [column.otherVolumeReceived],
        [column.baseOilAddition],
        [column.startingBaseOil],
        [column.receivedBaseOil],
        [column.endingBaseOil],
        [column.usedBaseOil],
    ];

    volumeReport.setFontStyle('normal');
    textArrayInBoxes(
        additionArr,
        tempLeft,
        MM.ADDITIONS_TOP + MM.ROW_SECTION_HEADER_HEIGHT,
        MM.COLUMN_WIDTH,
        MM.ADDITIONS_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT,
        'right',
        volumeReport
    );
    
    
    const lossesArr = [
        [column.lossToSurface],
        [column.lossToFormation],
        [column.solidsLoss],
        [column.lossToCuttings],
        [column.mudLeftInHole],
        [column.otherLoss],
        [column.plantVolumeReturned],
        [column.otherVolumeTransfered],
        undefined,
    ];
    
    textArrayInBoxes(
        lossesArr,
        tempLeft,
        MM.LOSSES_TOP + MM.ROW_SECTION_HEADER_HEIGHT,
        MM.COLUMN_WIDTH,
        MM.LOSSES_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT,
        'right',
        volumeReport
    );

    volumeReport.setFontStyle('bold');
    const additionsTotal = column.additionsTotal && typeof column.additionsTotal === 'number' ? column.additionsTotal.toFixed(0) : '';
    const lossesTotal = column.lossesTotal && typeof column.lossesTotal === 'number' ? column.lossesTotal.toFixed(0) : '';
    const finalVolume = column.finalVolume && typeof column.finalVolume === 'number' ? column.finalVolume.toFixed(0) : '';
    textRightJustifiedInBox(additionsTotal, tempLeft, MM.ADDITIONS_TOP, MM.COLUMN_WIDTH, MM.ROW_SECTION_HEADER_HEIGHT, volumeReport);
    textRightJustifiedInBox(lossesTotal, tempLeft, MM.LOSSES_TOP, MM.COLUMN_WIDTH, MM.ROW_SECTION_HEADER_HEIGHT, volumeReport);
    textRightJustifiedInBox(finalVolume, tempLeft, MM.FINAL_VOL_TOP, MM.COLUMN_WIDTH, MM.FINAL_VOL_HEIGHT, volumeReport);

};

const createWrapUpColumn = (wellCloseReview: IWellCloseReview, column: VolAcctColumn, colNum:number, externalStorageList: string[], volumeReport: jsPdf) => {
    const tempLeft = MM.COLUMN_START_LEFT + MM.COLUMN_WIDTH * colNum + 0.5;
    const va = wellCloseReview.volumeAccounting;
    volumeReport.setFillColor(MM.HEADER_COLOR_LIGHTER);
    volumeReport.rect(
        tempLeft,
        MM.ROW_HEADERS_BIG_TOP,
        MM.COLUMN_WIDTH,
        MM.ROW_HEADERS_BIG_HEIGHT + MM.EXTERNAL_STORAGE_HEIGHT + MM.ADDITIONS_HEIGHT + MM.LOSSES_HEIGHT + MM.FINAL_VOL_HEIGHT + MM.SECTION_GAP_WIDTH * 4,
        'F'
    );
        
    volumeReport.setFillColor(MM.HEADER_COLOR_LIGHT);
    volumeReport.rect(
        tempLeft + MM.COLUMN_WIDTH,
        MM.ROW_HEADERS_BIG_TOP,
        MM.COLUMN_WIDTH,
        MM.ROW_HEADERS_BIG_HEIGHT + MM.EXTERNAL_STORAGE_HEIGHT + MM.ADDITIONS_HEIGHT + MM.LOSSES_HEIGHT + MM.FINAL_VOL_HEIGHT + MM.SECTION_GAP_WIDTH * 4,
        'F'
    );

    volumeReport.setDrawColor('#000000');
    volumeReport.line(
        tempLeft,
        MM.ROW_HEADERS_BIG_TOP,
        tempLeft,
        MM.ROW_HEADERS_BIG_TOP + MM.ROW_HEADERS_BIG_HEIGHT + MM.EXTERNAL_STORAGE_HEIGHT + MM.ADDITIONS_HEIGHT + MM.LOSSES_HEIGHT + MM.FINAL_VOL_HEIGHT + MM.SECTION_GAP_WIDTH * 4
    );

    volumeReport.line(
        tempLeft + MM.COLUMN_WIDTH,
        MM.ROW_HEADERS_BIG_TOP,
        tempLeft + MM.COLUMN_WIDTH,
        MM.ROW_HEADERS_BIG_TOP + MM.ROW_HEADERS_BIG_HEIGHT + MM.EXTERNAL_STORAGE_HEIGHT + MM.ADDITIONS_HEIGHT + MM.LOSSES_HEIGHT + MM.FINAL_VOL_HEIGHT + MM.SECTION_GAP_WIDTH * 4
    );

    volumeReport.line(
        tempLeft + MM.COLUMN_WIDTH * 2,
        MM.ROW_HEADERS_BIG_TOP,
        tempLeft + MM.COLUMN_WIDTH * 2,
        MM.ROW_HEADERS_BIG_TOP + MM.ROW_HEADERS_BIG_HEIGHT + MM.EXTERNAL_STORAGE_HEIGHT + MM.ADDITIONS_HEIGHT + MM.LOSSES_HEIGHT + MM.FINAL_VOL_HEIGHT + MM.SECTION_GAP_WIDTH * 4
    );

    const headerArrBoldCentered = [
        ['Well', 'Wrap'],
        ['Close', 'Up'],
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        [undefined],
    ];

    const headerArrBoldRight = [
        [undefined],
        [undefined],
        [undefined],
        [va.volOwedToPlant, va.volOwedToPlant],
        [column.finalVolume, column.finalVolume],
        [undefined],
        [undefined],
        [undefined],
    ];

    const headerArrNormalRight = [
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        [0, column.newHoleVolume],
        [0, column.footageDrilled],
    ];
    volumeReport.setFontStyle('bold');
    textArrayInBoxes(
        headerArrBoldCentered,
        tempLeft,
        MM.ROW_HEADERS_BIG_TOP,
        MM.COLUMN_WIDTH * 2 - 1,
        MM.ROW_HEADERS_BIG_HEIGHT,
        'center',
        volumeReport
    );

    textArrayInBoxes(
        headerArrBoldRight,
        tempLeft,
        MM.ROW_HEADERS_BIG_TOP,
        MM.COLUMN_WIDTH * 2,
        MM.ROW_HEADERS_BIG_HEIGHT,
        'right',
        volumeReport
    );
    volumeReport.setFontStyle('normal');
    textArrayInBoxes(
        headerArrNormalRight,
        tempLeft,
        MM.ROW_HEADERS_BIG_TOP,
        MM.COLUMN_WIDTH * 2,
        MM.ROW_HEADERS_BIG_HEIGHT,
        'right',
        volumeReport
    );

    drawColLines(MM.COLUMN_START_LEFT + MM.COLUMN_WIDTH * colNum, MM.COLUMN_WIDTH, volumeReport);
    drawColLines(MM.COLUMN_START_LEFT + MM.COLUMN_WIDTH * ( colNum + 1 ), MM.COLUMN_WIDTH, volumeReport);

    const externalStorageArr = popExternalStorageForCol(externalStorageList, column);
    fillEmptyRows(externalStorageArr, MM.EXTERNAL_STORAGE_ROWS);
    textArrayInBoxes(
        externalStorageArr,
        tempLeft,
        MM.EXTERNAL_STORAGE_TOP + MM.ROW_SECTION_HEADER_HEIGHT,
        MM.COLUMN_WIDTH,
        MM.EXTERNAL_STORAGE_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT,
        'right',
        volumeReport
    );

    textArrayInBoxes(
        externalStorageArr,
        tempLeft + MM.COLUMN_WIDTH,
        MM.EXTERNAL_STORAGE_TOP + MM.ROW_SECTION_HEADER_HEIGHT,
        MM.COLUMN_WIDTH,
        MM.EXTERNAL_STORAGE_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT,
        'right',
        volumeReport
    );

    const sum = externalStorageArr.reduce((total, curr) => {
        const currNumber = curr[0];
        if (currNumber) {
            return total + currNumber;
        }
        return total;
    }, 0);
    volumeReport.setFontStyle('bold');
    textRightJustifiedInBox(sum.toFixed(0), tempLeft, MM.EXTERNAL_STORAGE_TOP, MM.COLUMN_WIDTH, MM.ROW_SECTION_HEADER_HEIGHT, volumeReport);
    textRightJustifiedInBox(sum.toFixed(0), tempLeft + MM.COLUMN_WIDTH, MM.EXTERNAL_STORAGE_TOP, MM.COLUMN_WIDTH, MM.ROW_SECTION_HEADER_HEIGHT, volumeReport);    

    const additionArr = [
        [va.water, Number(va.water) + column.waterAddition],
        [va.chemicalAdditions, Number(va.chemicalAdditions) + column.chemicalAddition],
        [va.slurry, Number(va.slurry) + column.slurryAddition],
        [va.waterFlow, Number(va.waterFlow) + column.washoutAddition],
        [va.otherAdditions, Number(va.otherAdditions) + column.otherAddition],
        [0, column.plantVolumeReceived],
        [0, column.otherVolumeReceived],
        [va.baseOil, Number(va.baseOil) + column.baseOilAddition],
        [va.startingBaseOil, 0],
        [va.baseOilReceived, Number(va.baseOilReceived) + column.receivedBaseOil],
        [va.endingBaseOil, Number(va.endingBaseOil) + column.endingBaseOil],
        [va.baseOilUsed, Number(va.baseOilUsed) + column.usedBaseOil],
    ];

    volumeReport.setFontStyle('normal');
    textArrayInBoxes(
        additionArr,
        tempLeft,
        MM.ADDITIONS_TOP + MM.ROW_SECTION_HEADER_HEIGHT,
        MM.COLUMN_WIDTH * 2,
        MM.ADDITIONS_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT,
        'right',
        volumeReport
    );
    
    
    const lossesArr = [
        [va.lossToSurface, Number(va.lossToSurface) + column.lossToSurface],
        [va.lossToFormation, Number(va.lossToFormation) + column.lossToFormation],
        [va.solidsControl, Number(va.solidsControl) + column.solidsLoss],
        [0, column.lossToCuttings],
        [va.mudLeftInHole, Number(va.mudLeftInHole) + column.mudLeftInHole],
        [va.otherLoss, Number(va.otherLoss) + column.otherLoss],
        [va.plantVolReturned, Number(va.plantVolReturned) + column.plantVolumeReturned],
        [va.otherVolTransferred, Number(va.otherVolTransferred) + column.otherVolumeTransfered],
        undefined,
    ];
    
    textArrayInBoxes(
        lossesArr,
        tempLeft,
        MM.LOSSES_TOP + MM.ROW_SECTION_HEADER_HEIGHT,
        MM.COLUMN_WIDTH * 2,
        MM.LOSSES_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT,
        'right',
        volumeReport
    );

    volumeReport.setFontStyle('bold');
    const additionsTotal = va.totalAdditions && typeof va.totalAdditions === 'number' ? va.totalAdditions.toFixed(0) : '';
    const lossesTotal = va.totalLosses && typeof va.totalLosses === 'number' ? va.totalLosses.toFixed(0) : '';
    const finalVolume = va.reconciliationFactor && typeof va.reconciliationFactor === 'number' ? va.reconciliationFactor.toFixed(0) : '';
    textRightJustifiedInBox(additionsTotal, tempLeft, MM.ADDITIONS_TOP, MM.COLUMN_WIDTH, MM.ROW_SECTION_HEADER_HEIGHT, volumeReport);
    textRightJustifiedInBox(lossesTotal, tempLeft, MM.LOSSES_TOP, MM.COLUMN_WIDTH, MM.ROW_SECTION_HEADER_HEIGHT, volumeReport);
    textRightJustifiedInBox(finalVolume, tempLeft, MM.FINAL_VOL_TOP, MM.COLUMN_WIDTH, MM.FINAL_VOL_HEIGHT, volumeReport);

    textRightJustifiedInBox((Number(additionsTotal) + Number(column.additionsTotal)).toFixed(0), tempLeft + MM.COLUMN_WIDTH, MM.ADDITIONS_TOP, MM.COLUMN_WIDTH, MM.ROW_SECTION_HEADER_HEIGHT, volumeReport);
    textRightJustifiedInBox((Number(lossesTotal) + Number(column.lossesTotal)).toFixed(0), tempLeft + MM.COLUMN_WIDTH, MM.LOSSES_TOP, MM.COLUMN_WIDTH, MM.ROW_SECTION_HEADER_HEIGHT, volumeReport);
    textRightJustifiedInBox(column.finalVolume.toFixed(0), tempLeft + MM.COLUMN_WIDTH, MM.FINAL_VOL_TOP, MM.COLUMN_WIDTH, MM.FINAL_VOL_HEIGHT, volumeReport);

};

const popExternalStorageForCol = (externalStorageList: string[], volAcctCol: VolAcctColumn) => {
    const retArr: Array<Array<number | undefined>> = [];
    externalStorageList.forEach(value => {
        const items = volAcctCol.externalStorage.filter(es => es.fluidType === value);
        const sum = items.reduce((total, curr) => {
            if (curr.inUse) {
                return total + Number(curr.todayFluidAmount);
            }
            return total;
        }, 0);
        retArr.push([sum]);
    });

    return retArr;
};

const popRowHeaders = (volumeReport: jsPdf) => {
    const bigBoldHeaders = [
        ['Fluid Type'],
        ['Test Date'],
        [undefined],
        ['Volume Owed To Plant'],
        ['Starting Report Volume'],
        [undefined],
        ['New Hole Volume'],
        ['Footage Drilled'],
    ];
    volumeReport.setFontStyle('bold');
    textArrayInBoxes(bigBoldHeaders, MM.ROW_HEADERS_BIG_LEFT, MM.ROW_HEADERS_BIG_TOP, MM.ROW_HEADERS_BIG_WIDTH, MM.ROW_HEADERS_BIG_HEIGHT, 'left', volumeReport);
    
};

const popExternalHeaders = (storageList: string[], volumeReport: jsPdf) => {
    const headerText = 'External Storage (bbls)';
    const storageText: any = [];
    storageList.forEach(value => storageText.push([value]));
    textLeftJustifiedInBox(headerText, MM.EXTERNAL_STORAGE_LEFT, MM.EXTERNAL_STORAGE_TOP, MM.EXTERNAL_STORAGE_WIDTH, MM.ROW_SECTION_HEADER_HEIGHT, volumeReport);
    volumeReport.setFontStyle('normal');
    volumeReport.setFontSize(MM.HEADER_2_SIZE);
    fillEmptyRows(storageText, MM.EXTERNAL_STORAGE_ROWS);
    textArrayInBoxes(storageText, MM.EXTERNAL_STORAGE_LEFT + MM.INDENT_WITDH, MM.EXTERNAL_STORAGE_TOP + MM.ROW_SECTION_HEADER_HEIGHT, MM.EXTERNAL_STORAGE_WIDTH, MM.EXTERNAL_STORAGE_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT, 'left', volumeReport);
};

const popAdditionsHeaders = (volumeReport: jsPdf) => {
    const headerText = 'Additions (bbls)';
    const additionsText = [
        ['Water'],
        ['Chemicals'],
        ['Slurry'],
        ['Water Flow'],
        ['Other Additions'],
        ['Plant Volume Received'],
        ['Other Volume Received'],
        ['Base Oil']
    ];
    
    const baseOilText = [
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        [undefined],
        ['Starting (gal)'],
        ['Received (gal)'],
        ['Ending (gal)'],
        ['Used (gal)'],
    ];

    volumeReport.setFontStyle('bold');
    volumeReport.setFontSize(MM.HEADER_1_SIZE);
    textLeftJustifiedInBox(headerText, MM.ADDITIONS_LEFT, MM.ADDITIONS_TOP, MM.ADDITIONS_WIDTH, MM.ROW_SECTION_HEADER_HEIGHT, volumeReport);
    
    volumeReport.setFontStyle('normal');
    volumeReport.setFontSize(MM.HEADER_2_SIZE);
    fillEmptyRows(additionsText, MM.ADDITIONS_ROWS + MM.ADDITION_BASE_ROWS);
    textArrayInBoxes(
        additionsText,
        MM.ADDITIONS_LEFT + MM.INDENT_WITDH,
        MM.ADDITIONS_TOP + MM.ROW_SECTION_HEADER_HEIGHT,
        MM.ADDITIONS_WIDTH - MM.INDENT_WITDH,
        MM.ADDITIONS_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT,
        'left',
        volumeReport
    );

    textArrayInBoxes(
        baseOilText,
        MM.ADDITIONS_LEFT + MM.INDENT_WITDH * 2,
        MM.ADDITIONS_TOP + MM.ROW_SECTION_HEADER_HEIGHT,
        MM.ADDITIONS_WIDTH - MM.INDENT_WITDH * 2,
        MM.ADDITIONS_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT,
        'left',
        volumeReport
    );


};

const popLossesHeaders = (volumeReport: jsPdf) => {
    const headerText = 'Losses (bbls)';
    const lossesText = [
        ['Loss to Surface'],
        ['Loss to Formation'],
        ['Solids Control'],
        ['Loss to Cuttings'],
        ['Mud Left in Hole'],
        ['Other Losses'],
        ['Plant Volume Returned'],
        ['Other Volume Transfered'],
        undefined,
    ];

    volumeReport.setFontStyle('bold');
    volumeReport.setFontSize(MM.HEADER_1_SIZE);
    textLeftJustifiedInBox(headerText, MM.LOSSES_LEFT, MM.LOSSES_TOP, MM.LOSSES_WIDTH, MM.ROW_SECTION_HEADER_HEIGHT, volumeReport);
    
    volumeReport.setFontStyle('normal');
    volumeReport.setFontSize(MM.HEADER_2_SIZE);
    
    textArrayInBoxes(
        lossesText,
        MM.LOSSES_LEFT + MM.INDENT_WITDH,
        MM.LOSSES_TOP + MM.ROW_SECTION_HEADER_HEIGHT,
        MM.LOSSES_WIDTH - MM.INDENT_WITDH,
        MM.LOSSES_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT,
        'left',
        volumeReport
    );
};

const popFinalVolHeader = (volumeReport: jsPdf) => {
    const headerText = "Final Volume (bbl)";
    volumeReport.setFontStyle('bold');
    volumeReport.setFontSize(MM.HEADER_1_SIZE);    
    textLeftJustifiedInBox(headerText, MM.FINAL_VOL_LEFT, MM.FINAL_VOL_TOP, MM.FINAL_VOL_WIDTH, MM.FINAL_VOL_HEIGHT, volumeReport);
    volumeReport.setFontStyle('normal');
    volumeReport.setFontSize(MM.HEADER_2_SIZE);
};
const drawHeaderLines = (volumeReport: jsPdf) => {
    volumeReport.setLineWidth(MM.THIN_LINE);
    volumeReport.line(
        MM.EXTERNAL_STORAGE_LEFT,
        MM.EXTERNAL_STORAGE_TOP + MM.ROW_SECTION_HEADER_HEIGHT - MM.LINE_Y_OFFSET,
        MM.EXTERNAL_STORAGE_LEFT + MM.EXTERNAL_STORAGE_WIDTH,
        MM.EXTERNAL_STORAGE_TOP + MM.ROW_SECTION_HEADER_HEIGHT - MM.LINE_Y_OFFSET
    );

    volumeReport.line(
        MM.ADDITIONS_LEFT,
        MM.ADDITIONS_TOP + MM.ROW_SECTION_HEADER_HEIGHT - MM.LINE_Y_OFFSET,
        MM.ADDITIONS_LEFT + MM.ADDITIONS_WIDTH,
        MM.ADDITIONS_TOP + MM.ROW_SECTION_HEADER_HEIGHT - MM.LINE_Y_OFFSET
    );

    volumeReport.line(
        MM.ADDITIONS_LEFT + MM.INDENT_WITDH,
        MM.ADDITION_BASE_OIL_BOTTOM,
        MM.ADDITIONS_LEFT + MM.ADDITIONS_WIDTH,
        MM.ADDITION_BASE_OIL_BOTTOM
    );

    volumeReport.line(
        MM.LOSSES_LEFT,
        MM.LOSSES_TOP + MM.ROW_SECTION_HEADER_HEIGHT - MM.LINE_Y_OFFSET,
        MM.LOSSES_LEFT + MM.LOSSES_WIDTH,
        MM.LOSSES_TOP + MM.ROW_SECTION_HEADER_HEIGHT - MM.LINE_Y_OFFSET
    );

    volumeReport.line(
        MM.FINAL_VOL_LEFT,
        MM.FINAL_VOL_TOP,
        MM.FINAL_VOL_LEFT + MM.FINAL_VOL_WIDTH,
        MM.FINAL_VOL_TOP
    );

    volumeReport.setLineWidth(MM.THICK_LINE);

    volumeReport.line(
        MM.FINAL_VOL_LEFT,
        MM.FINAL_VOL_BOTTOM,
        MM.FINAL_VOL_LEFT + MM.FINAL_VOL_WIDTH,
        MM.FINAL_VOL_BOTTOM
    );
};

const drawColLines = (left: number, width:number, volumeReport: jsPdf) => {
    volumeReport.setLineWidth(MM.THIN_LINE);
    volumeReport.line(
        left,
        MM.EXTERNAL_STORAGE_TOP + MM.ROW_SECTION_HEADER_HEIGHT - MM.LINE_Y_OFFSET,
        left + width + MM.LINE_X_OFFSET,
        MM.EXTERNAL_STORAGE_TOP + MM.ROW_SECTION_HEADER_HEIGHT - MM.LINE_Y_OFFSET
    );

    volumeReport.line(
        left,
        MM.ADDITIONS_TOP + MM.ROW_SECTION_HEADER_HEIGHT - MM.LINE_Y_OFFSET,
        left + width + MM.LINE_X_OFFSET,
        MM.ADDITIONS_TOP + MM.ROW_SECTION_HEADER_HEIGHT - MM.LINE_Y_OFFSET
    );

    volumeReport.line(
        left,
        MM.ADDITION_BASE_OIL_BOTTOM,
        left + width + MM.LINE_X_OFFSET,
        MM.ADDITION_BASE_OIL_BOTTOM
    );

    volumeReport.line(
        left,
        MM.LOSSES_TOP + MM.ROW_SECTION_HEADER_HEIGHT - MM.LINE_Y_OFFSET,
        left + width + MM.LINE_X_OFFSET,
        MM.LOSSES_TOP + MM.ROW_SECTION_HEADER_HEIGHT - MM.LINE_Y_OFFSET
    );

    volumeReport.line(
        left,
        MM.FINAL_VOL_TOP,
        left + width + MM.LINE_X_OFFSET,
        MM.FINAL_VOL_TOP
    );

    volumeReport.setLineWidth(MM.THICK_LINE);

    volumeReport.line(
        left,
        MM.FINAL_VOL_BOTTOM,
        left + width + MM.LINE_X_OFFSET,
        MM.FINAL_VOL_BOTTOM
    );
};




// WEBPACK FOOTER //
// ./src/Services/Pdf/VolumeAccountingReport/DrawReportVolume.ts